import { useState, useMemo, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import SASidebar from '@/pages/sa/dashboard/components/SASidebar';
import PaymentDetailModal from './components/PaymentDetailModal';
import ManualPaymentModal from './components/ManualPaymentModal';
import {
  payments as mockPayments,
  methodLabels,
  paymentStatusLabels,
  paymentStatusColors,
  type Payment,
} from '@/mocks/superAdminPayments';

type LoadingState = 'loading' | 'ready' | 'error';
type PaymentStatus = Payment['status'];
type PaymentMethod = Payment['method'];

const ALL_STATUSES: (PaymentStatus | 'todos')[] = ['todos', 'paid', 'pending', 'failed', 'refunded', 'manual_review', 'cancelled'];

function formatCurrency(n: number): string {
  return `$${n.toFixed(2)}`;
}

const methodIcon: Record<PaymentMethod, string> = {
  stripe: 'ri-bank-card-line',
  paypal: 'ri-paypal-line',
  square: 'ri-apps-2-line',
  manual: 'ri-user-settings-line',
};

const methodChip: Record<PaymentMethod, string> = {
  stripe: 'bg-emerald-50 text-emerald-600',
  paypal: 'bg-sky-50 text-sky-600',
  square: 'bg-indigo-50 text-indigo-600',
  manual: 'bg-foreground-100 text-foreground-600',
};

function simulateLoad(): Promise<Payment[]> {
  return new Promise((resolve, reject) => {
    setTimeout(() => {
      if (Math.random() > 0.94) reject(new Error('Timeout al conectar con el servidor.'));
      else resolve(mockPayments);
    }, 600);
  });
}

function showSweetAlert(icon: 'success' | 'error' | 'warning' | 'info', title: string, text?: string) {
  return Swal.fire({
    icon, title, text: text ?? '',
    confirmButtonColor: '#1a1a1a',
    customClass: { popup: 'rounded-2xl text-sm', title: 'text-sm font-bold', confirmButton: 'text-xs font-medium px-4 py-2 rounded-xl' },
  });
}

export default function SuperAdminPayments() {
  const navigate = useNavigate();
  const [data, setData] = useState<Payment[]>([]);
  const [loadingState, setLoadingState] = useState<LoadingState>('loading');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const [activeStatus, setActiveStatus] = useState<string>('todos');
  const [searchTerm, setSearchTerm] = useState('');

  const [viewPayment, setViewPayment] = useState<Payment | null>(null);
  const [showManualForm, setShowManualForm] = useState(false);
  const [exportModalOpen, setExportModalOpen] = useState(false);

  const loadData = useCallback(() => {
    setLoadingState('loading'); setErrorMsg(null);
    simulateLoad().then((d) => { setData(d); setLoadingState('ready'); }).catch((err) => { setErrorMsg(err.message || 'Error desconocido'); setLoadingState('error'); });
  }, []);
  useEffect(() => { loadData(); }, [loadData]);

  const statusCounts = useMemo(() => {
    const c: Record<string, number> = { todos: data.length };
    ALL_STATUSES.filter((s) => s !== 'todos').forEach((s) => { c[s] = data.filter((p) => p.status === s).length; });
    return c;
  }, [data]);

  const filtered = useMemo(() => {
    let r = data;
    if (activeStatus !== 'todos') r = r.filter((p) => p.status === activeStatus);
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      r = r.filter((p) => p.radioName.toLowerCase().includes(q) || p.frequency.toLowerCase().includes(q) || p.reference.toLowerCase().includes(q));
    }
    return r;
  }, [data, activeStatus, searchTerm]);

  const handleMarkPaid = (p: Payment) => {
    setData((prev) => prev.map((x) => (x.id === p.id ? { ...x, status: 'paid' as PaymentStatus, reviewed: true } : x)));
    showSweetAlert('success', 'Marcado como pagado', `El pago ${p.reference} ahora está confirmado.`);
  };

  const handleRetry = (p: Payment) => {
    setData((prev) => prev.map((x) => (x.id === p.id ? { ...x, status: 'pending' as PaymentStatus, reviewed: false } : x)));
    showSweetAlert('success', 'Reintentado', 'Se volvió a procesar el cobro.');
  };

  const handleRefund = (p: Payment) => {
    Swal.fire({
      icon: 'warning', title: '¿Reembolsar?', text: `${formatCurrency(p.amount)} de ${p.radioName}.`,
      showCancelButton: true, confirmButtonText: 'Reembolsar', cancelButtonText: 'Cancelar',
      confirmButtonColor: '#dc2626', cancelButtonColor: '#e5e5e5',
      customClass: { popup: 'rounded-2xl text-sm', title: 'text-sm font-bold', confirmButton: 'text-xs font-medium px-4 py-2 rounded-xl', cancelButton: 'text-xs font-medium px-4 py-2 rounded-xl text-foreground-700' },
    }).then((r) => { if (r.isConfirmed) { setData((prev) => prev.map((x) => x.id === p.id ? { ...x, status: 'refunded' as PaymentStatus } : x)); showSweetAlert('success', 'Reembolsado', 'El pago fue reembolsado.'); } });
  };

  const handleCancel = (p: Payment) => {
    Swal.fire({
      icon: 'warning', title: '¿Cancelar?', text: `El pago ${p.reference} será cancelado.`,
      showCancelButton: true, confirmButtonText: 'Cancelar pago', cancelButtonText: 'Volver',
      confirmButtonColor: '#6b7280', cancelButtonColor: '#e5e5e5',
      customClass: { popup: 'rounded-2xl text-sm', title: 'text-sm font-bold', confirmButton: 'text-xs font-medium px-4 py-2 rounded-xl', cancelButton: 'text-xs font-medium px-4 py-2 rounded-xl text-foreground-700' },
    }).then((r) => { if (r.isConfirmed) { setData((prev) => prev.map((x) => x.id === p.id ? { ...x, status: 'cancelled' as PaymentStatus } : x)); showSweetAlert('success', 'Cancelado', 'El pago fue cancelado.'); } });
  };

  const handleCreateManual = (payment: Payment) => {
    setData((prev) => [payment, ...prev]);
    setShowManualForm(false);
  };

  const handleExport = (format: string) => {
    Swal.fire({ icon: 'info', title: `Exportando ${format.toUpperCase()}`, text: `Archivo ${format} generado (mock).`, toast: true, position: 'top-end', showConfirmButton: false, timer: 2000, timerProgressBar: true, customClass: { popup: 'rounded-2xl text-sm', title: 'text-xs font-semibold' } });
    setExportModalOpen(false);
  };

  const nextId = useMemo(() => Math.max(...data.map((p) => p.id), 0) + 1, [data]);

  return (
    <div className="min-h-screen bg-background-100">
      <div className="flex">
        <SASidebar />
        <main className="flex-1 min-w-0">
          <div className="p-4 md:p-6 lg:p-8 pb-8 space-y-5 max-w-[1600px] mx-auto">
            {/* Breadcrumb */}
            <div className="flex items-center gap-1.5 text-[10px] text-foreground-400">
              <Link to="/sa/dashboard" className="hover:text-foreground-600 transition-colors cursor-pointer">Dashboard</Link>
              <i className="ri-arrow-right-s-line text-[9px]"></i>
              <Link to="/sa/billing" className="hover:text-foreground-600 transition-colors cursor-pointer">Billing</Link>
              <i className="ri-arrow-right-s-line text-[9px]"></i>
              <span className="text-foreground-500 font-medium">Pagos</span>
            </div>

            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h2 className="text-foreground-950 font-bold text-base">Pagos</h2>
                <p className="text-foreground-400 text-xs mt-0.5">{loadingState === 'ready' ? `${data.length} pagos` : 'Cargando...'}</p>
              </div>
              <div className="flex items-center gap-2">
                <div className="relative w-full sm:w-52">
                  <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-xs"></i>
                  <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Buscar radio, ref..."
                    disabled={loadingState !== 'ready'}
                    className="w-full pl-8 pr-8 py-2 bg-background-50 border border-background-200 rounded-xl text-xs text-foreground-800 placeholder:text-foreground-300 outline-none focus:border-foreground-300 transition-colors disabled:opacity-50" />
                  {searchTerm && <button onClick={() => setSearchTerm('')} className="absolute right-2 top-1/2 -translate-y-1/2 w-5 h-5 rounded-md bg-background-200 flex items-center justify-center cursor-pointer hover:bg-background-300 transition-colors"><i className="ri-close-line text-foreground-500 text-[10px]"></i></button>}
                </div>
                <button onClick={() => setShowManualForm(true)} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-foreground-950 text-background-50 text-xs font-bold hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-add-line text-sm"></i>Registrar pago
                </button>
                <button onClick={() => setExportModalOpen(true)} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-background-200 bg-background-50 text-foreground-600 text-xs font-semibold hover:bg-background-100 transition-all cursor-pointer whitespace-nowrap">
                  <i className="ri-download-line text-sm"></i>Exportar
                </button>
              </div>
            </div>

            {/* Loading */}
            {loadingState === 'loading' && (
              <div className="bg-background-50 border border-background-200 rounded-2xl overflow-hidden">
                <div className="divide-y divide-background-100">
                  {Array.from({ length: 6 }).map((_, i) => (
                    <div key={i} className="flex items-center gap-4 px-4 py-3 animate-pulse">
                      <div className="w-32 h-3 bg-background-200 rounded"></div>
                      <div className="w-16 h-3 bg-background-200 rounded"></div>
                      <div className="w-20 h-3 bg-background-200 rounded"></div>
                      <div className="w-16 h-5 bg-background-200 rounded-full ml-auto"></div>
                      <div className="w-20 h-3 bg-background-200 rounded"></div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Error */}
            {loadingState === 'error' && (
              <div className="bg-background-50 border border-background-200 rounded-2xl p-12 text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-red-50 flex items-center justify-center"><i className="ri-error-warning-line text-red-400 text-2xl"></i></div>
                <p className="text-foreground-800 text-sm font-semibold mb-1">Error al cargar datos</p><p className="text-foreground-400 text-xs mb-4">{errorMsg}</p>
                <button onClick={loadData} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-foreground-950 text-background-50 text-xs font-bold hover:bg-foreground-800 transition-colors cursor-pointer"><i className="ri-refresh-line text-sm"></i>Reintentar</button>
              </div>
            )}

            {/* Status chips */}
            {loadingState === 'ready' && (
              <div className="flex flex-wrap items-center gap-1">
                {ALL_STATUSES.map((s) => (
                  <button key={s} onClick={() => setActiveStatus(s)} className={`px-2.5 py-1 rounded-full text-[10px] font-medium transition-colors cursor-pointer whitespace-nowrap ${activeStatus === s ? 'bg-foreground-950 text-background-50' : 'bg-background-50 text-foreground-500 hover:bg-background-200 border border-background-200'}`}>
                    {s === 'todos' ? 'Todas' : paymentStatusLabels[s as PaymentStatus]} ({statusCounts[s] ?? 0})
                  </button>
                ))}
              </div>
            )}

            {/* Table */}
            {loadingState === 'ready' && (
              filtered.length === 0 ? (
                <div className="bg-background-50 border border-background-200 rounded-2xl p-12 text-center">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-background-100 flex items-center justify-center"><i className="ri-inbox-line text-foreground-300 text-2xl"></i></div>
                  <p className="text-foreground-800 text-sm font-semibold mb-1">Sin resultados</p>
                  <button onClick={() => { setActiveStatus('todos'); setSearchTerm(''); }} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-background-200 text-foreground-600 text-xs font-bold hover:bg-background-300 transition-colors cursor-pointer"><i className="ri-filter-off-line text-sm"></i>Limpiar filtros</button>
                </div>
              ) : (
                <div className="bg-background-50 border border-background-200 rounded-2xl overflow-hidden">
                  {/* Desktop */}
                  <div className="hidden lg:block">
                    <table className="w-full text-left">
                      <thead>
                        <tr className="border-b border-background-200 bg-background-100/50">
                          {['Radio', 'Monto', 'Método', 'Estado', 'Fecha', 'Acciones'].map((h) => (
                            <th key={h} className="px-4 py-2.5 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-background-100">
                        {filtered.map((p) => (
                          <tr key={p.id} onClick={() => setViewPayment(p)} className="hover:bg-background-100/50 transition-colors cursor-pointer">
                            <td className="px-4 py-3">
                              <p className="text-foreground-800 text-xs font-semibold">{p.radioName}</p>
                              <p className="text-foreground-400 text-[10px]">{p.frequency}</p>
                            </td>
                            <td className="px-4 py-3">
                              <span className="text-foreground-800 text-xs font-bold">{formatCurrency(p.amount)}</span>
                            </td>
                            <td className="px-4 py-3">
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${methodChip[p.method]}`}>
                                <i className={`${methodIcon[p.method]} text-[9px]`}></i>
                                {methodLabels[p.method]}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${paymentStatusColors[p.status]}`}>
                                {paymentStatusLabels[p.status]}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <span className="text-foreground-600 text-xs">{p.date}</span>
                            </td>
                            <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                              <button onClick={() => setViewPayment(p)} className="w-7 h-7 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors" title="Ver detalle">
                                <i className="ri-eye-line text-foreground-500 text-xs"></i>
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile */}
                  <div className="lg:hidden divide-y divide-background-100">
                    {filtered.map((p) => (
                      <div key={p.id} onClick={() => setViewPayment(p)} className="p-4 cursor-pointer hover:bg-background-100/50 transition-colors">
                        <div className="flex items-center justify-between mb-1.5">
                          <p className="text-foreground-800 text-xs font-semibold">{p.radioName}</p>
                          <span className="text-foreground-800 text-xs font-bold">{formatCurrency(p.amount)}</span>
                        </div>
                        <div className="flex items-center justify-between mb-1.5">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${methodChip[p.method]}`}>
                            <i className={`${methodIcon[p.method]} text-[9px]`}></i>
                            {methodLabels[p.method]}
                          </span>
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${paymentStatusColors[p.status]}`}>
                            {paymentStatusLabels[p.status]}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-foreground-400 text-[10px]">{p.date}</span>
                          <button onClick={(e) => { e.stopPropagation(); setViewPayment(p); }} className="w-7 h-7 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors" title="Ver detalle">
                            <i className="ri-eye-line text-foreground-500 text-xs"></i>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )
            )}
          </div>
        </main>
      </div>

      {/* Detail Modal */}
      {viewPayment && (
        <PaymentDetailModal
          payment={viewPayment}
          onClose={() => setViewPayment(null)}
          onMarkPaid={handleMarkPaid}
          onRetry={handleRetry}
          onRefund={handleRefund}
          onCancel={handleCancel}
          onNavigate={(path) => navigate(path)}
        />
      )}

      {/* Manual Form */}
      <ManualPaymentModal
        open={showManualForm}
        onClose={() => setShowManualForm(false)}
        onCreatePayment={handleCreateManual}
        nextId={nextId}
      />

      {/* Export Modal */}
      {exportModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setExportModalOpen(false)}></div>
          <div className="relative bg-background-50 rounded-2xl shadow-xl w-full max-w-xs">
            <div className="flex items-center justify-between px-5 py-4 border-b border-background-200"><h3 className="text-foreground-950 font-bold text-sm">Exportar Pagos</h3><button onClick={() => setExportModalOpen(false)} className="w-7 h-7 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200"><i className="ri-close-line text-foreground-500 text-sm"></i></button></div>
            <div className="p-5 space-y-2">
              {[
                { format: 'csv', icon: 'ri-file-excel-2-line', color: 'bg-emerald-50 text-emerald-600', label: 'CSV' },
                { format: 'excel', icon: 'ri-file-excel-2-line', color: 'bg-emerald-50 text-emerald-600', label: 'Excel (.xlsx)' },
                { format: 'pdf', icon: 'ri-file-pdf-2-line', color: 'bg-red-50 text-red-500', label: 'PDF' },
              ].map(exp => (
                <button key={exp.format} onClick={() => handleExport(exp.format)} className={`w-full flex items-center gap-3 p-3 rounded-xl ${exp.color.split(' ')[0]} hover:opacity-80 transition-opacity cursor-pointer text-left`}>
                  <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center"><i className={`${exp.icon} ${exp.color.split(' ')[1]} text-sm`}></i></div>
                  <span className={`text-xs font-semibold ${exp.color.split(' ')[1]}`}>{exp.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}